import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, MapPin, CheckCircle, Package, Heart, Cake, Diamond, Flower2, Phone, Mail, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';

const MarketingHome = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const appUrl = 'https://app.flowersin2hrs.co.za/explore';
  const loginUrl = 'https://app.flowersin2hrs.co.za/login';
  const vendorUrl = 'https://app.flowersin2hrs.co.za/register?vendor=true';
  const driverUrl = 'https://app.flowersin2hrs.co.za/register?driver=true';
  const experiencesUrl = 'https://app.flowersin2hrs.co.za/experiences';
  
  // Experience category URLs
  const experienceUrls = {
    romantic: 'https://app.flowersin2hrs.co.za/experiences/romance',
    birthday: 'https://app.flowersin2hrs.co.za/experiences/birthday',
    proposal: 'https://app.flowersin2hrs.co.za/experiences/proposal',
    apology: 'https://app.flowersin2hrs.co.za/experiences/apology',
  };

  return (
    <div className="marketing-site">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_608776ba-04ae-48e0-9ffc-286ce70bf202/artifacts/hq0sgvai_Flowers_in_2_Hrs.png" 
                alt="Flowers in 2 Hrs" 
                className="h-12 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How It Works</button>
              <button onClick={() => scrollToSection('experiences')} className="nav-link">Experiences</button>
              <button onClick={() => scrollToSection('vendors')} className="nav-link">For Vendors</button>
              <button onClick={() => scrollToSection('drivers')} className="nav-link">For Drivers</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
              <Button 
                onClick={() => window.location.href = loginUrl}
                variant="outline"
                className="border-primary-teal text-primary-teal hover:bg-primary-teal hover:text-white"
              >
                Login
              </Button>
              <Button 
                onClick={() => window.location.href = appUrl}
                className="bg-primary-teal hover:bg-primary-teal-dark transition-all"
              >
                Place an Order
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-white border-t">
              <div className="flex flex-col space-y-4 px-6 py-6">
                <button onClick={() => scrollToSection('how-it-works')} className="nav-link-mobile">How It Works</button>
                <button onClick={() => scrollToSection('experiences')} className="nav-link-mobile">Experiences</button>
                <button onClick={() => scrollToSection('vendors')} className="nav-link-mobile">For Vendors</button>
                <button onClick={() => scrollToSection('drivers')} className="nav-link-mobile">For Drivers</button>
                <button onClick={() => scrollToSection('contact')} className="nav-link-mobile">Contact</button>
                <Button 
                  onClick={() => window.location.href = loginUrl}
                  variant="outline"
                  className="border-primary-teal text-primary-teal hover:bg-primary-teal hover:text-white w-full"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => window.location.href = appUrl}
                  className="bg-primary-teal hover:bg-primary-teal-dark w-full"
                >
                  Place an Order
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="hero-section pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="hero-title">
                Send flowers and gifts in 2 hours
              </h1>
              <p className="hero-subtitle">
                Fast, reliable gifting and experiences delivered on demand across South Africa
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => window.location.href = appUrl}
                  className="bg-primary-teal hover:bg-primary-teal-dark cta-button"
                  size="lg"
                >
                  Place an Order <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button 
                  onClick={() => scrollToSection('how-it-works')}
                  variant="outline"
                  size="lg"
                  className="border-primary-teal text-primary-teal hover:bg-primary-teal hover:text-white"
                >
                  Learn More
                </Button>
              </div>
              <div className="flex items-center space-x-8 pt-4">
                <div className="flex items-center space-x-2">
                  <Clock className="text-primary-teal" size={24} />
                  <span className="trust-badge">2-Hour Delivery</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="text-primary-teal" size={24} />
                  <span className="trust-badge">Nationwide</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="hero-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fast-flower-delivery/artifacts/gov3rdp6_E503798A-CD14-4A19-B916-3E1C02BA5356.png" 
                  alt="Flower delivery - Driver handing beautiful bouquet to customer"
                  className="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="section-padding bg-accent-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Simple, fast, and reliable</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">
                <Package size={40} />
              </div>
              <h3 className="step-title">Choose Your Gift</h3>
              <p className="step-description">
                Browse our curated selection of premium flowers, gifts, and experiences
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">
                <MapPin size={40} />
              </div>
              <h3 className="step-title">Enter Delivery Details</h3>
              <p className="step-description">
                Provide recipient information and select your delivery time
              </p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">
                <CheckCircle size={40} />
              </div>
              <h3 className="step-title">Track in Real-Time</h3>
              <p className="step-description">
                Monitor your delivery every step of the way until it arrives
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => window.location.href = appUrl}
              className="bg-primary-teal hover:bg-primary-teal-dark cta-button"
              size="lg"
            >
              Place an Order <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Experiences Section */}
      <section id="experiences" className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Curated Experiences</h2>
            <p className="section-subtitle">Unforgettable moments, delivered on demand</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Romantic */}
            <div 
              className="experience-card"
              onClick={() => window.location.href = experienceUrls.romantic}
              style={{ cursor: 'pointer' }}
            >
              <div className="experience-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1774233429558-d31d3b516c7c" 
                  alt="Romantic Experience"
                  className="experience-image"
                />
                <div className="experience-overlay">
                  <Heart className="experience-icon" size={48} />
                </div>
              </div>
              <div className="experience-content">
                <h3 className="experience-title">Romantic</h3>
                <p className="experience-description">
                  Create unforgettable moments with curated romantic packages
                </p>
              </div>
            </div>

            {/* Birthday */}
            <div 
              className="experience-card"
              onClick={() => window.location.href = experienceUrls.birthday}
              style={{ cursor: 'pointer' }}
            >
              <div className="experience-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1519654793190-2e8a4806f1f2" 
                  alt="Birthday Experience"
                  className="experience-image"
                />
                <div className="experience-overlay">
                  <Cake className="experience-icon" size={48} />
                </div>
              </div>
              <div className="experience-content">
                <h3 className="experience-title">Birthday</h3>
                <p className="experience-description">
                  Make their special day extraordinary with perfect celebrations
                </p>
              </div>
            </div>

            {/* Proposal */}
            <div 
              className="experience-card"
              onClick={() => window.location.href = experienceUrls.proposal}
              style={{ cursor: 'pointer' }}
            >
              <div className="experience-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1773457220441-0542558cdacd" 
                  alt="Proposal Experience"
                  className="experience-image"
                />
                <div className="experience-overlay">
                  <Diamond className="experience-icon" size={48} />
                </div>
              </div>
              <div className="experience-content">
                <h3 className="experience-title">Proposal</h3>
                <p className="experience-description">
                  Picture-perfect setups for your once-in-a-lifetime moment
                </p>
              </div>
            </div>

            {/* Apology */}
            <div 
              className="experience-card"
              onClick={() => window.location.href = experienceUrls.apology}
              style={{ cursor: 'pointer' }}
            >
              <div className="experience-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1677274174240-d2d58f36c641" 
                  alt="Apology Experience"
                  className="experience-image"
                />
                <div className="experience-overlay">
                  <Flower2 className="experience-icon" size={48} />
                </div>
              </div>
              <div className="experience-content">
                <h3 className="experience-title">Apology</h3>
                <p className="experience-description">
                  Thoughtful gestures to mend hearts and strengthen bonds
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => window.location.href = experiencesUrl}
              className="bg-primary-teal hover:bg-primary-teal-dark cta-button"
              size="lg"
            >
              Browse Experiences <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-accent-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Why Choose Us</h2>
            <p className="section-subtitle">Speed, reliability, and convenience you can trust</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="feature-card">
              <Clock className="feature-icon" size={48} />
              <h3 className="feature-title">2-Hour Delivery</h3>
              <p className="feature-description">
                Lightning-fast delivery when you need it most. Order now, delivered within 2 hours.
              </p>
            </div>

            <div className="feature-card">
              <CheckCircle className="feature-icon" size={48} />
              <h3 className="feature-title">100% Reliable</h3>
              <p className="feature-description">
                Track every step of your delivery in real-time. We're committed to on-time arrivals.
              </p>
            </div>

            <div className="feature-card">
              <MapPin className="feature-icon" size={48} />
              <h3 className="feature-title">Nationwide Coverage</h3>
              <p className="feature-description">
                Operating across South Africa. Premium gifting wherever you need it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Vendors Section */}
      <section id="vendors" className="section-padding vendor-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.pexels.com/photos/14656012/pexels-photo-14656012.jpeg" 
                alt="Become a vendor"
                className="vendor-image"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="section-title text-left">For Vendors</h2>
              <p className="vendor-subtitle">
                Sell your products and services on-demand
              </p>
              <p className="vendor-description">
                Join our growing network of premium vendors and reach customers across South Africa. 
                Expand your business with our fast, reliable delivery platform.
              </p>
              <ul className="vendor-benefits space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-primary-teal mt-1 flex-shrink-0" size={20} />
                  <span>Access to a nationwide customer base</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-primary-teal mt-1 flex-shrink-0" size={20} />
                  <span>Streamlined order management system</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-primary-teal mt-1 flex-shrink-0" size={20} />
                  <span>Real-time delivery tracking and support</span>
                </li>
              </ul>
              <Button 
                onClick={() => window.location.href = vendorUrl}
                className="bg-primary-teal hover:bg-primary-teal-dark cta-button"
                size="lg"
              >
                Become a Vendor <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* For Drivers Section */}
      <section id="drivers" className="section-padding bg-accent-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="section-title text-left">For Drivers</h2>
              <p className="vendor-subtitle">
                Earn by delivering flowers, gifts, and experiences on your schedule
              </p>
              <p className="vendor-description">
                Join a fast-growing platform with real demand. Flexible hours, competitive earnings, 
                and the satisfaction of delivering joy to people across South Africa.
              </p>
              <ul className="vendor-benefits space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-primary-teal mt-1 flex-shrink-0" size={20} />
                  <span>Work on your own schedule</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-primary-teal mt-1 flex-shrink-0" size={20} />
                  <span>Competitive earnings per delivery</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-primary-teal mt-1 flex-shrink-0" size={20} />
                  <span>Real-time order notifications and routing</span>
                </li>
              </ul>
              <Button 
                onClick={() => window.location.href = driverUrl}
                className="bg-primary-teal hover:bg-primary-teal-dark cta-button"
                size="lg"
              >
                Become a Driver <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
            <div className="order-1 lg:order-2">
              <img 
                src="https://images.pexels.com/photos/8931670/pexels-photo-8931670.jpeg" 
                alt="Become a driver"
                className="vendor-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-accent-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">Get In Touch</h2>
            <p className="section-subtitle">We're here to help</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="contact-card">
              <Mail className="contact-icon" size={32} />
              <h3 className="contact-title">Email Us</h3>
              <a href="mailto:info@flowersin2hrs.co.za" className="contact-link">
                info@flowersin2hrs.co.za
              </a>
            </div>

            <div className="contact-card">
              <Phone className="contact-icon" size={32} />
              <h3 className="contact-title">Call Us</h3>
              <a href="tel:+27750076416" className="contact-link">
                +27 75 007 6416
              </a>
            </div>
          </div>

          <div className="text-center mt-12 p-6 bg-white rounded-lg shadow-sm">
            <MapPin className="text-primary-teal mx-auto mb-3" size={32} />
            <p className="font-semibold text-lg">Operating nationwide across South Africa</p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding final-cta-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="final-cta-title">Ready to Send Joy?</h2>
          <p className="final-cta-subtitle">
            Fast, reliable flower and gift delivery in just 2 hours
          </p>
          <Button 
            onClick={() => window.location.href = appUrl}
            className="bg-white text-primary-teal hover:bg-gray-100 cta-button-large"
            size="lg"
          >
            Place an Order <ArrowRight className="ml-2" size={24} />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_608776ba-04ae-48e0-9ffc-286ce70bf202/artifacts/hq0sgvai_Flowers_in_2_Hrs.png" 
                alt="Flowers in 2 Hrs" 
                className="h-12 w-auto mb-4"
              />
              <p className="footer-description">
                Fast, reliable gifting and experiences delivered on demand across South Africa.
              </p>
            </div>

            <div>
              <h4 className="footer-heading">Quick Links</h4>
              <ul className="footer-links">
                <li><button onClick={() => scrollToSection('how-it-works')}>How It Works</button></li>
                <li><button onClick={() => scrollToSection('experiences')}>Experiences</button></li>
                <li><button onClick={() => scrollToSection('vendors')}>For Vendors</button></li>
                <li><button onClick={() => scrollToSection('drivers')}>For Drivers</button></li>
                <li><button onClick={() => scrollToSection('contact')}>Contact</button></li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Contact</h4>
              <ul className="footer-contact">
                <li>
                  <a href="mailto:info@flowersin2hrs.co.za">info@flowersin2hrs.co.za</a>
                </li>
                <li>
                  <a href="tel:+27750076416">+27 75 007 6416</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 Flowers in 2 Hrs. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingHome;
